import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { apiClient, ApiError, handleApiError } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userType: string | null;
  guestId: string | null;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [error, setError] = useState<string | null>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [guestId, setGuestId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.checkAuth();
      setIsAuthenticated(response.authenticated);
      if (response.authenticated) {
        setUserType(response.userType || null);
        setGuestId(response.guestId || null);
      } else {
        setUserType(null);
        setGuestId(null);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUserType(null);
      setGuestId(null);
      // Don't set error for auth check failure - just means not authenticated
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Handle page visibility changes to re-check auth (helpful for iOS)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && hasInitialized) {
        // Re-check auth when page becomes visible, but don't show loading
        checkAuth();
      }
    };

    const handleOnline = () => {
      if (hasInitialized) {
        // Re-check auth when coming back online
        checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkAuth, hasInitialized]);

  const login = useCallback(async (password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.authenticate(password);
      
      if (response.authenticated) {
        setIsAuthenticated(true);
        setUserType(response.userType || null);
        setGuestId(response.guestId || null);
        return true;
      } else {
        setError(response.message || 'Authentication failed');
        return false;
      }
    } catch (error) {
      if (error instanceof ApiError) {
        // Check if it's the specific invalid credentials error
        if (error.message.includes('Invalid credentials') || error.message.includes('provided password is incorrect')) {
          setError('Incorrect password');
        } else if (error.status === 400) {
          setError('Incorrect password');
        } else {
          setError(handleApiError(error));
        }
      } else {
        setError(handleApiError(error));
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = () => {
    apiClient.logout();
    setIsAuthenticated(false);
    setUserType(null);
    setGuestId(null);
    setError(null);
  };

  // Removed automatic auth check on mount - now handled in useEffect above

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    userType,
    guestId,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 